import React, { useState, useEffect } from 'react';
import HeaderMobile from './HeaderMobile.js';
import Header from './Header.js';
import Sidebar from './Sidebar.js';
import { useNavigate, Link } from 'react-router-dom';
import Footer from './Footer.js';
import { Spinner, Alert } from 'react-bootstrap';
import { BASE_URL } from './Constants.js';

const Manage_DressSize = () => {
  const [users, setUsers] = useState([]); // Store active dress sizes
  const [filteredUsers, setFilteredUsers] = useState([]); // Store filtered dress sizes based on search
  const [user, setUser] = useState(null); // Store user profile data
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(''); // Store search query
  const [itemsPerPage, setItemsPerPage] = useState(10); // Items per page for pagination
  const [currentPage, setCurrentPage] = useState(1); // Current page for pagination

  const navigate = useNavigate(); // For navigating programmatically

  // Fetch user profile to check if user is admin
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No token found');
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        const response = await fetch(`${BASE_URL}profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        setUser(data.profile.is_uniform_access_given); // Set admin status based on the response
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Fetch users (dress sizes) when the component mounts
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('No token found');
          setLoading(false);
          return;
        }

        const response = await fetch(`${BASE_URL}dress_sizes`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        if (data && data.dressSizes && Array.isArray(data.dressSizes)) {
          const activeDressSizes = data.dressSizes.filter(dressSize => dressSize.status === 'A');
          setUsers(activeDressSizes);
          setFilteredUsers(activeDressSizes);
        } else {
          setError('No dress sizes found');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Handle search functionality
  const handleSearch = (e) => {
  const query = e.target.value.toLowerCase();
  setSearchQuery(query);

  const filteredData = users.filter(user =>
    (user.dress_size && user.dress_size.toLowerCase().includes(query)) ||
    (user.dress_type && user.dress_type.toLowerCase().includes(query))
  );

  setFilteredUsers(filteredData);
  setCurrentPage(1); // Reset to the first page on search
};

  // Handle page change for pagination
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle change in items per page for pagination
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page on change of items per page
  };

  // Handle delete functionality (deactivating a dress size)
  const handleDelete = (id) => {
    const confirmDelete = window.confirm('Are you sure you want to deactivate this dress size?');
    if (confirmDelete) {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('id', id);

      fetch(`${BASE_URL}delete_dresssize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      })
        .then(response => response.json())
        .then(data => {
          if (data.status) {
            // Refresh the page after successful deletion
            window.location.reload();
          } else {
            alert('Failed to deactivate the dress size.');
          }
        })
        .catch(err => alert('An error occurred while deactivating the dress size.'));
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  if (loading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <Alert variant="danger">{error}</Alert>
      </div>
    );
  }
  const handleToggleSidebar = () => {
    if (window.innerWidth >= 992) {
      setIsSidebarOpen(prev => !prev);
    }
  };
  return (
    <>
      <div className="page-wrapper">
        <HeaderMobile />
        <Sidebar isOpen={isSidebarOpen} />
        <div className="page-container"
          style={{
            marginLeft: isSidebarOpen && window.innerWidth >= 992 ? '300px' : '100px',
            transition: 'margin-left 0.3s ease',
            zIndex: 2000,
            position: 'relative',
          }}
        >
          <Header onToggleSidebar={handleToggleSidebar} isSidebarOpen={isSidebarOpen} />
          <div className="main-content">
            <div className="section__content section__content--p30">
              <div className="container-fluid">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      {user === 'Y' && (
                        <button
                          className="btn btn-primary btn-lg"
                          onClick={() => navigate('/add_dresssize')}
                        >
                          <i className="fas fa-plus"></i> Add Dress Size
                        </button>
                      )}
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <input
                        type="text"
                        className="form-control me-3"
                        placeholder="Search Dress Size"
                        value={searchQuery}
                        onChange={handleSearch}
                      />
                      <div className="d-flex align-items-center">
                        <label htmlFor="rowsPerPage" className="form-label me-2">Rows:</label>
                        <select
                          id="rowsPerPage"
                          className="form-control"
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={30}>30</option>
                        </select>
                      </div>
                    </div>
                    <div className="table-responsive table--no-card m-b-30">
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>S/N</th>
                            <th>Dress Size</th>
                            <th>Dress Type</th>
                            <th>Gender</th>
                            <th>Edit</th>
                            <th>Delete</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentUsers.length > 0 ? (
                            currentUsers.map((user, index) => (
                              <tr key={user.dress_size}>
                                <td>{index + 1}</td>
                                <td>{user.dress_size}</td>
                                <td>{user.dress_type}</td>
                                <td>{user.gender}</td>
                                <td>
                                  <Link to={`/get_dress_sizes/${user.id}`}>
                                    <i className="fa fa-edit" style={{ fontSize: '12px', color: 'green' }}></i>
                                  </Link>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <button
                                    onClick={() => handleDelete(user.id)}
                                    style={{ color: 'red' }}
                                  >
                                    <i className="fas fa-trash-alt" style={{ fontSize: '18px' }}></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr><td colSpan="4" className="text-center">No dress sizes found</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="d-flex justify-content-center mt-3">
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="btn btn-sm btn-outline-secondary mx-1"
                      >
                        Prev
                      </button>
                      {[...Array(totalPages).keys()].map(pageNumber => (
                        <button
                          key={pageNumber}
                          onClick={() => paginate(pageNumber + 1)}
                          className={`btn btn-sm ${currentPage === pageNumber + 1 ? 'btn-primary' : 'btn-outline-secondary'} mx-1`}
                        >
                          {pageNumber + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="btn btn-sm btn-outline-secondary mx-1"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default Manage_DressSize;
